import type { CSSProperties } from 'react';
import type { TocItem, FaqItem } from '@/components/editorial/Blocks';
import type { Source } from '@/components/editorial/AuthorByline';
import type { Reviewer } from '@/components/editorial/authors';

/** The three editorial pillars surfaced on the homepage. */
export type GuideCategoryId = 'financial' | 'visa' | 'life';

export interface GuideCategory {
  id: GuideCategoryId;
  label: string;
  labelKo: string;
  /** One line explaining what questions this pillar answers. */
  blurb: string;
  /** Obangsaek hex owned by this pillar. Use for fills, never for small text. */
  color: string;
  colorDark: string;
  /**
   * Accessible text colour on light surfaces.
   *
   * Hwang (#FFB81C) is ~1.8:1 on white and fails WCAG AA badly, so the Expat
   * Life pillar uses the darker Hwang-700 (#B87800, ~4.6:1) wherever the colour
   * carries words rather than area. Jeok and Cheong pass as-is.
   */
  textColor: string;
  textColorDark: string;
  /** Text colour to use ON `color` as a background — yellow needs dark ink. */
  onColor: string;
  icon: 'wallet' | 'stamp' | 'globe';
}

export const GUIDE_CATEGORIES: Record<GuideCategoryId, GuideCategory> = {
  financial: {
    id: 'financial',
    label: 'Financial & Remittance',
    labelKo: '금융 · 송금',
    blurb: 'Transfer fees, exchange rates, tax reporting, and banking across borders.',
    color: '#0047AB',
    colorDark: '#3B7DD8',
    textColor: '#0047AB',
    textColorDark: '#3B7DD8',
    onColor: '#FFFFFF',
    icon: 'wallet',
  },
  visa: {
    id: 'visa',
    label: 'Visa & Legal',
    labelKo: '비자 · 법률',
    blurb: 'F-4 and residency routes, paperwork, nationality, and military service.',
    color: '#C8102E',
    colorDark: '#B00E28',
    textColor: '#C8102E',
    textColorDark: '#E8737E',
    onColor: '#FFFFFF',
    icon: 'stamp',
  },
  life: {
    id: 'life',
    label: 'Expat Life & Culture',
    labelKo: '해외 생활 · 문화',
    blurb: 'Settling in, finding your people, careers, and raising bilingual kids.',
    color: '#FFB81C',
    colorDark: '#FFB81C',
    textColor: '#B87800',
    textColorDark: '#FFD45A',
    onColor: '#1C1C1C',
    icon: 'globe',
  },
};

/**
 * Exposes a category's palette as CSS custom properties.
 *
 * Inline `style` cannot express a dark-mode variant, so components spread these
 * vars onto the element and reference them with Tailwind arbitrary values
 * (`text-[var(--cat-text)] dark:text-[var(--cat-text-dark)]`). That keeps the
 * light/dark pair in one place instead of branching on a theme value in JS.
 */
export function categoryVars(c: GuideCategory): CSSProperties {
  return {
    '--cat-color': c.color,
    '--cat-color-dark': c.colorDark,
    '--cat-text': c.textColor,
    '--cat-text-dark': c.textColorDark,
    '--cat-on': c.onColor,
  } as CSSProperties;
}

export interface GuideMeta {
  slug: string;
  category: GuideCategoryId;

  /** H1 / <title>. Written for the query, not for cleverness. */
  title: string;
  titleKo: string;
  /** Shorter label for cards and breadcrumbs. */
  shortTitle: string;
  /** Meta description — 140–160 chars. */
  description: string;
  /** Card excerpt. Can be longer than the meta description. */
  excerpt: string;

  readingTimeMinutes: number;
  /** ISO date (YYYY-MM-DD). */
  publishedAt: string;
  updatedAt: string;
  /** When a human last checked every figure against a primary source. */
  lastVerifiedAt: string;

  authorId: string;
  reviewer?: Reviewer;
  factChecked: boolean;

  toc: TocItem[];
  faq: FaqItem[];
  sources: Source[];
  keywords: string[];
  /** YMYL disclaimer shown in the E-E-A-T block. */
  disclaimer?: string;

  /** Tailwind gradient classes for the card/hero when there's no photo. */
  gradient: string;
  /** Slugs of related guides, rendered at the foot of the article. */
  related: string[];
}
